// Inline approval card shown above the composer when THIS thread's agent has
// parked on an Allowance (e.g. "send a WhatsApp as you"). Without it, a request
// raised from the web app only surfaces on the channel / the Allowance page — the
// turn just sits "Working…" with nothing to act on. This polls the person's
// pending allowances, keeps the ones tied to the open thread, and lets them
// Approve / Deny right here. Resolving unblocks the parked agent so it continues.
import { ShieldCheckIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl } from "../../apiBase";
import { useMorrowSignal } from "../../hooks/useMorrowSignal";

type Allowance = { id: string; action: string; title: string; threadId: string | null };

async function postResolve(id: string, decision: "approve" | "deny"): Promise<void> {
  try {
    await fetch(apiUrl("/api/morrow/allowances/resolve"), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ id, decision }),
    });
  } catch {
    /* the poll reflects the true state */
  }
}

export function ThreadApprovalBanner({ threadId }: { threadId: string | null }) {
  const [items, setItems] = useState<Allowance[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!threadId) {
      setItems([]);
      return;
    }
    // Don't poll a backgrounded tab — this ran every 3s in EVERY open chat and was a
    // major source of /allowances load hammering the API.
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const res = await fetch(apiUrl("/api/morrow/allowances"), {
        headers: { accept: "application/json" },
      });
      if (res.ok) {
        const data = (await res.json()) as { allowances?: Allowance[] };
        const all = Array.isArray(data.allowances) ? data.allowances : [];
        setItems(all.filter((a) => a.threadId === threadId));
      }
    } catch {
      /* ignore; the poll retries */
    }
  }, [threadId]);

  useEffect(() => {
    void refresh();
    // Push-driven now (useMorrowSignal below); this is only a slow backstop for a
    // missed poke. Was 15s in EVERY open chat — a major /allowances load source.
    pollRef.current = setInterval(() => void refresh(), 90000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);
  // Instant refetch when the server pokes that an allowance changed — but only if
  // it's for this thread (or thread-agnostic), so other threads don't refetch here.
  useMorrowSignal("allowances", (tick) => {
    if (tick.threadId == null || tick.threadId === threadId) void refresh();
  });

  const resolve = useCallback(async (id: string, decision: "approve" | "deny") => {
    setBusyId(id);
    await postResolve(id, decision);
    setBusyId(null);
    // Optimistically drop it; the poll confirms.
    setItems((prev) => prev.filter((a) => a.id !== id));
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="mb-2 flex flex-col gap-2">
      {items.map((a) => (
        <div
          key={a.id}
          className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3.5 py-3"
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <ShieldCheckIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-foreground">Approval needed</p>
              <p className="truncate text-[13px] text-muted-foreground">{a.title}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-border bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              disabled={busyId === a.id}
              onClick={() => void resolve(a.id, "approve")}
            >
              Approve
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60"
              disabled={busyId === a.id}
              onClick={() => void resolve(a.id, "deny")}
            >
              Deny
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
